/**
 * The four tech slots on a `ticket_line_items` row — who the field says worked
 * this line, as distinct from who has been assigned pay for it.
 *
 * Confirmed against `ticketLineItems` in the API's `src/db/schema.ts`: four
 * pairs, `assigned_tech_1_id` … `assigned_tech_4_id` and
 * `assigned_tech_1_initials` … `assigned_tech_4_initials`. `GET /tickets/:id`
 * spreads the rows onto the ticket untransformed, so reads see snake_case; the
 * camelCase alternatives here are the read-tolerance rule in CLAUDE.md.
 *
 * **A slot counts as named when it has an id OR initials, and the OR is the
 * whole point.** The id columns are not populated for every tenant —
 * `pay-ai.service.ts` carries a measured note that its rateless-worker guard is
 * "for the tenants whose `assigned_tech_*_id` columns have not populated yet",
 * and the lines it measured were all Smith's. Smith is precisely where this was
 * reported (FT-00055, four lines showing "Pays nobody" while three carried
 * `TM`, `CH` and `WG/RD/BS`). Reading only the id side would have reproduced the
 * bug on the exact ticket it was reported against.
 *
 * The API reads both sides too, in `assignedTechIdsOf` and
 * `assignedTechInitialsOf`. This mirrors that split rather than inventing a
 * third opinion about who worked a line.
 *
 * No normalisation, no deduplication. A slot's initials are free text and are
 * not always one person — TNT's FT-04403 has `CS` in two slots for two
 * different Salyers, and Smith writes `WG/RD/BS` into a single slot. Presence is
 * all this answers; the API owns matching a token to a human.
 */

export interface TechSlot {
  /** 1-4, the slot's position on the row. */
  slot: number;
  id: string | null;
  initials: string | null;
}

interface SlotSource {
  [key: string]: unknown;
}

const SLOTS = [1, 2, 3, 4] as const;

function readSlot(li: SlotSource, n: number): TechSlot | null {
  const id = (li[`assigned_tech_${n}_id`] ?? li[`assignedTech${n}Id`]) as string | null | undefined;
  const rawInitials = (li[`assigned_tech_${n}_initials`] ??
    li[`assignedTech${n}Initials`]) as string | null | undefined;

  // A slot holding only whitespace is an empty slot. It reaches the column as a
  // real string, so a bare truthiness check would call it named and render a
  // blank chip.
  const initials = typeof rawInitials === 'string' && rawInitials.trim() !== ''
    ? rawInitials.trim()
    : null;
  const techId = typeof id === 'string' && id.trim() !== '' ? id.trim() : null;

  if (!techId && !initials) return null;
  return { slot: n, id: techId, initials };
}

/** The occupied tech slots on a line, in slot order. Empty slots are skipped. */
export function techSlotsOf(li: unknown): TechSlot[] {
  if (!li || typeof li !== 'object') return [];
  const source = li as SlotSource;
  return SLOTS.map((n) => readSlot(source, n)).filter((s): s is TechSlot => s !== null);
}

/**
 * Is anyone named on this line?
 *
 * This is NOT "has anyone been paid for it". A line can name three techs and
 * carry no pay assignments at all — that is every line on every ticket before
 * AI Suggest has run, and it is the state this distinction exists to separate
 * from a line that genuinely names nobody.
 */
export function hasNamedTech(li: unknown): boolean {
  return techSlotsOf(li).length > 0;
}
